<script lang="ts">
    import {
        VpCalendarIcon,
        VpGroupIcon,
        VpMapPinIcon,
        VpRefreshIcon,
        VpReturnIcon,
        VpRunIcon,
    } from '@decathlon-experience/icons/svelte';
    import dayjs from 'dayjs';

    import { trans } from '$assets/shop-translator.js';
    import Button from '$saga/components/Button.svelte';
    import {
        filteredToursCount,
        filtersContext,
        mainFiltersModalOpen,
    } from '$saga/views/Search/store';
    import { FilterType } from '$saga/views/Search/types';
    import { handleResetValues } from '$saga/views/Search/utils';

    export let handleSelectedFilter: (filterType: FilterType) => void;
</script>

<div class="flex h-full w-full flex-col">
    <div
        class="no-scrollbar flex h-full w-full flex-col gap-m overflow-y-scroll"
    >
        <div
            class="w-full px-m pt-m text-center text-[22px] font-semibold desktop:text-[26px]"
        >
            {#if $filteredToursCount !== null}
                <div class="italic text-content-brand">
                    {$filteredToursCount}
                    {$filteredToursCount > 1
                        ? trans('search.bar.trip_plural')
                        : trans('search.bar.trip_singular')}
                </div>
                {$filteredToursCount > 1
                    ? trans('search.bar.matches_your_wishes_plural')
                    : trans('search.bar.matches_your_wishes_singular')}
            {/if}
        </div>

        <button
            type="button"
            class="flex w-full items-center justify-between gap-m self-center bg-container-quiet px-xs py-l hover:bg-container-commercial-neutral"
            on:click={() => handleSelectedFilter(FilterType.SPORT)}
        >
            <span class="flex items-center gap-xs">
                <VpRunIcon class="size-l" />
                {trans('search.bar.sport')}
            </span>
            {#if $filtersContext.formLabels.theme.length > 0}
                {#if $filtersContext.formValues.theme.length === 1 && $filtersContext.formValues.theme[0] === 'all'}
                    <span class="truncate text-sm text-content-brand">
                        {trans('search.bar.all_sports')}
                    </span>
                {:else}
                    <span class="truncate text-sm text-content-brand">
                        {$filtersContext.formLabels.theme.join(', ')}
                    </span>
                {/if}
            {:else}
                <span class="text-sm text-content-quiet"
                    >{trans('search.bar.add_sports')}</span
                >
            {/if}
        </button>

        <button
            type="button"
            class="flex w-full items-center justify-between gap-m self-center bg-container-quiet px-xs py-l hover:bg-container-commercial-neutral"
            on:click={() => handleSelectedFilter(FilterType.DESTINATION)}
        >
            <span class="flex items-center gap-xs">
                <VpMapPinIcon class="size-l" />
                {trans('search.bar.destination')}
            </span>
            {#if $filtersContext.formLabels.destination.length > 0}
                {#if $filtersContext.formValues.destination.length === 1 && $filtersContext.formValues.destination[0] === 'all'}
                    <span class="truncate text-sm text-content-brand">
                        {trans('search.bar.all_destinations')}
                    </span>
                {:else}
                    <span class="truncate text-sm text-content-brand">
                        {$filtersContext.formLabels.destination.join(', ')}
                    </span>
                {/if}
            {:else}
                <span class="text-sm text-content-quiet"
                    >{trans('search.bar.add_destinations')}</span
                >
            {/if}
        </button>

        <button
            type="button"
            class="flex w-full items-center justify-between gap-m self-center bg-container-quiet px-xs py-l hover:bg-container-commercial-neutral"
            on:click={() => handleSelectedFilter(FilterType.STARTDATE)}
        >
            <span class="flex items-center gap-xs">
                <VpCalendarIcon class="size-l" />
                {trans('search.bar.departure_date')}
            </span>
            {#if $filtersContext.formLabels.startDate != null}
                <span class="whitespace-nowrap text-sm text-content-brand">
                    {dayjs(
                        $filtersContext.formLabels.startDate.toString(),
                    ).format('DD MMM YYYY')} - {dayjs(
                        $filtersContext.formLabels.endDate?.toString(),
                    ).format('DD MMM YYYY')}
                </span>
            {:else if $filtersContext.formLabels.months.length > 0}
                <span class="w-full truncate text-sm text-content-brand">
                    {$filtersContext.formLabels.months.join(', ')}
                </span>
            {:else}
                <span class="text-sm text-content-quiet"
                    >{trans('search.bar.add_departure')}
                </span>
            {/if}
        </button>
        <button
            type="button"
            class="flex w-full items-center justify-between gap-m self-center bg-container-quiet px-xs py-l hover:bg-container-commercial-neutral"
            on:click={() => handleSelectedFilter(FilterType.PAX)}
        >
            <span class="flex items-center gap-xs">
                <VpGroupIcon class="size-l" />
                {trans('search.bar.travelers')}
            </span>
            {#if $filtersContext.formLabels.nbPax !== '' && $filtersContext.formLabels.nbPax !== '0'}
                <span class="truncate text-sm text-content-brand">
                    {$filtersContext.formLabels.nbPax}
                    {Number($filtersContext.formLabels.nbPax) > 1
                        ? trans('search.bar.traveler_plural')
                        : trans('search.bar.traveler_singular')}
                </span>
            {:else}
                <span class="text-sm text-content-quiet"
                    >{trans('search.bar.add_traveler')}</span
                >
            {/if}
        </button>
    </div>

    <div
        class="sticky bottom-0 left-0 flex w-full justify-between border-t border-solid border-border-quiet bg-white py-s"
    >
        <Button
            size="small"
            intent="secondary"
            shape="rounded"
            type="button"
            on:click={() => handleResetValues()}
            startIcon={VpReturnIcon}
            >{trans('search.filter.sidebar.clear_all')}</Button
        >

        <Button
            type="submit"
            form="travel-search-engine-form"
            data-turbo={$filtersContext.isHomePage ? false : true}
            size="small"
            shape="rounded"
            on:click={() => {
                $mainFiltersModalOpen = false;
            }}
        >
            {#if $filtersContext.isSubmitting}
                <VpRefreshIcon class="size-m animate-spin " />
            {:else}
                {trans('search.bar.search')}
                {#if $filteredToursCount !== null}
                    ({$filteredToursCount}){/if}
            {/if}
        </Button>
    </div>
</div>
